<template>
  <div class="segments-page">
    <div :class="pageLoading ? 'hidden' : ''">
      <x-table
        :title="$t('ui.segments.segments')"
        :columns="columns"
        :tableData="segments"
        :enableSearch="true"
        :enableFilter="false"
        :totalResults="totalCount"
        :pagination="defaultPagination"
        @updatePagination="updatePagination"
        @fetchData="fetchData"
        :showLoader="loading"
        :actions="actions"
        actionsLocation="row"
        :rowsAreClickable="true"
        @clickRow="actionEdit"
        tableSettingsName="segments"
      ></x-table>
    </div>

    <div class="segments-page-loader" :class="pageLoading ? '' : 'hidden'">
      <loading></loading>
    </div>

    <modal
      :title="$t('ui.segments.duplicate_modal.title')"
      :saveText="$t('ui.segments.duplicate_modal.duplicate')"
      :open="duplicateSegmentModalOpen"
      @save="duplicateSegment()"
      @cancel="closeDuplicateSegmentModal()"
    >
      <field
        v-model="duplicateSegmentName"
        :label="$t('ui.segments.duplicate_modal.name')"
        :autofocus="true"
      />
    </modal>

    <modal
      :open="deleteSegmentModalOpen"
      :title="$t('ui.segments.delete_modal.title')"
      type="warning"
      :showCancelForWarning="true"
      :saveText="$t('ui.global.delete')"
      @save="deleteSegment"
      @cancel="closeDeleteSegmentModal"
    >
      <p>{{ $t('ui.global.delete_confirmation', { name: this.currentSegment ? this.currentSegment.name : '' }) }}</p>
    </modal>
  </div>
</template>
<script>

import XTable from "@/components/helpers/DataDisplay/XTable"
import SegmentApi from "@/api/Segments"
import Modal from '@/components/helpers/Layout/NewModal'
import Field from '@/components/helpers/DataInput/Field'
import Ui from "@/classes/Ui"
import i18n from "@/i18n"
import Loading from '@/components/helpers/Specific/Loading.vue'

export default {
  components: { XTable, Modal, Field, Loading },
  data() {
    return {
      segmentApi: new SegmentApi(),
      loading: false,
      pageLoading: true,
      totalCount: 0,
      defaultPagination: {
        sortBy: 'name',
        descending: false,
      },
      currentSegment: null,
      deleteSegmentModalOpen: false,
      segments: [],
      duplicateSegmentModalOpen: false,
      duplicateSegmentName: '',
      duplicateSegmentSource: null,
      columns: [
        {
          name: "name",
          field: segment => segment.name,
          label: i18n.t('ui.segments.columns.name'),
          required: true,
          isSticky: true,
          isBold: true,
        },
        {
          name: "id",
          label: i18n.t('ui.segments.columns.id'),
          field: contact => contact.id,
          credentials: "root",
          hideByDefault: true,
        },
        {
          name: "creator",
          field: segment => segment["user"].firstname,
          label: i18n.t('ui.segments.columns.creator'),
        },
        {
          name: "created_at",
          field: segment => segment.created_at,
          label: i18n.t('ui.segments.columns.created'),
          type: "dateTime",
        },
        {
          name: "updated_at",
          field: segment => segment.updated_at,
          type: "dateTime",
          label: i18n.t('ui.segments.columns.updated'),
        },
      ],
      actions: [
        {
          name: i18n.t('ui.segments.actions.edit'),
          description: i18n.t('ui.segments.actions.edit_description'),
          icon: 'yule-icon-edit',
          function: this.actionEdit,
        },
        {
          name: i18n.t('ui.segments.actions.duplicate'),
          description: i18n.t('ui.segments.actions.duplicate_description'),
          icon: 'yule-icon-duplicate',
          function: this.actionDuplicate,
        },
        {
          name: i18n.t('ui.segments.actions.delete'),
          description: i18n.t('ui.segments.actions.delete_description'),
          icon: 'yule-icon-trash',
          function: this.actionDelete,
        },
      ],
    }
  },
  beforeMount() {
    this.pageLoading = true
  },
  destroyed() {
    this.currentSegment = null;
  },
  mounted() {
    this.currentSegment = null;

    Ui.setPageLevel("2")
    Ui.setSecondaryHeaderTabs({})
    Ui.setTitle({ label: "ui.pages.segments" })
    Ui.setHeaderButtons([
      {
        name: "segment",
        action: "segments.new",
        classes: "btn-primary",
      },
    ])

    this.fetchData()
  },
  methods: {
    async actionDelete(ids = []) {
      if (ids.length != 1) {
        return
      }

      let id = ids[0];

      this.currentSegment = this.segments.find((segment) => { return segment.id == id });

      this.openDeleteSegmentModal();
    },
    async deleteSegment() {
      await this.segmentApi.deleteSegment(this.currentSegment.id)
      this.fetchData()
      this.closeDeleteSegmentModal()
    },
    async actionDuplicate(ids = []) {
      if (ids.length != 1) {
        return
      }
      let id = ids[0]

      this.duplicateSegmentSource = null
      this.segments.forEach((segment) => {
        if (segment.id == id) {
          this.duplicateSegmentSource = segment
        }
      })

      if (this.duplicateSegmentSource == null) {
        return
      }

      this.duplicateSegmentName = this.$t('ui.segments.actions.duplicate_name', { name: this.duplicateSegmentSource.name })

      this.openDuplicateSegmentModal()
    },
    actionEdit(ids = []) {
      let id = null
      if (Array.isArray(ids)) {
        if (ids.length != 1) {
          return
        }
        id = ids[0]
      } else {
        id = ids
      }
      this.$router.push({ name: 'segment_edit', params: { id:id } })
    },
    updatePagination(pagination) {
      // We just respond to the updatePagination event so that we
      // get the whole default pagination, including any XTable defaults
      this.defaultPagination = pagination
    },
    openDuplicateSegmentModal() {
      this.duplicateSegmentModalOpen = true
    },
    closeDuplicateSegmentModal() {
      this.duplicateSegmentModalOpen = false
    },
    openDeleteSegmentModal() {
      this.deleteSegmentModalOpen = true
    },
    closeDeleteSegmentModal() {
      this.deleteSegmentModalOpen = false
      this.currentSegment = null
    },
    async duplicateSegment() {

      let newSegment = {
        name: this.duplicateSegmentName,
        filters: this.duplicateSegmentSource.filters,
        search: this.duplicateSegmentSource.search,
      }

      let resource = await this.segmentApi.createSegment(newSegment)

      this.closeDuplicateSegmentModal()

      this.$router.push({ name: 'segment_edit', params: { id: resource.id } })
    },
    async fetchData(fetchParams = {}) {
      // Build the API request
      let request = {
        search: '',
        page: this.defaultPagination.page,
        per_page: this.defaultPagination.rowsPerPage,
        sort_by: this.defaultPagination.sortBy,
        sort_order: this.defaultPagination.descending ? 'DESC' : 'ASC'
      }

      if (fetchParams.searchKeywords) {
        request.search = fetchParams.searchKeywords
      }

      if (typeof fetchParams.pagination == 'object' && fetchParams.pagination) {
        request.per_page = fetchParams.pagination.rowsPerPage
        request.sort_by = fetchParams.pagination.sortBy
        request.sort_order = fetchParams.pagination.descending ? 'DESC' : 'ASC'
      }

      this.loading = true

      // Make the request
      let requestResponse = null
      try {
        requestResponse = await this.segmentApi.getSegments(request)
      } finally {
        this.loading = false
        this.pageLoading = false
      }

      // Display both the returned contacts page and the total number of contacts
      this.segments = requestResponse.data.resources
      this.totalCount = requestResponse.data.total_results
    }
  },
}
</script>

<style lang="sass" scoped>
.segments-page
  .segments-page-loader
    z-index: 200
    position: absolute
    top: 0
    left: 0
    margin: 0 auto
    width: 100%
    height: 100%
    &.hidden
      visibility: hidden
</style>



// WEBPACK FOOTER //
// Segments.vue?37851af9